import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { auth, db, googleProvider } from "firebaseConfig";
import { UserProfile } from "types/user";

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  role: number;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  signInWithGoogle: async () => {},
  signOut: async () => {},
  role: -1,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      if (!firebaseUser) {
        setProfile(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, "users", user.uid);

    // Realtime listener — creates doc on first visit if it doesn't exist
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setDoc(userRef, {
            uid: user.uid,
            email: user.email ?? "",
            displayName: user.displayName ?? "",
            photoURL: user.photoURL ?? "",
            role: -1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            approvedBy: null,
            approvedAt: null,
            lastLoginAt: null,
          })
            .then(() => {
              setProfile({
                uid: user.uid,
                email: user.email ?? "",
                displayName: user.displayName ?? "",
                photoURL: user.photoURL ?? "",
                role: -1,
                createdAt: serverTimestamp() as any,
                updatedAt: serverTimestamp() as any,
                approvedBy: null,
                approvedAt: null,
                lastLoginAt: null,
              });
              setLoading(false);
            })
            .catch((err) => {
              console.error("Failed to create user doc:", err);
              // If we can't create the doc, force logout to prevent infinite redirect loop
              console.error("User doc creation failed. Please check Firestore rules.");
              setUser(null);
              setProfile(null);
              setLoading(false);
            });
        } else {
          const profileData = snap.data() as UserProfile;
          setProfile(profileData);
          if (profileData.lastLoginAt) {
            try {
              const lastLoginMs = profileData.lastLoginAt.toDate().getTime();
              const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
              if (Date.now() - lastLoginMs > SEVEN_DAYS) {
                console.log("Session expired. Force re-login.");
                setUser(null);
                setProfile(null);
                setLoading(false);
                return;
              }
            } catch {
              // serverTimestamp proxy — skip check on first read
            }
          }
          setLoading(false);
        }
      },
      () => {
        // Firestore listener error — leave loading true to prevent a redirect loop
        // The redirect loop happens when loading=false but profile is still null
        console.error("Firestore listener error for user doc:", user.uid);
      }
    );

    return () => unsub();
  }, [user]);

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const { user: signedUser } = await signInWithPopup(auth, googleProvider);
      const userRef = doc(db, "users", signedUser.uid);
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });
    } catch (error) {
      console.error("Google sign-in error:", error);
      setLoading(false);
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setProfile(null);
  };

  const role = profile?.role ?? -1;

  return (
    <AuthContext.Provider value={{ user, profile, loading, signInWithGoogle, signOut, role }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useRole() {
  const { role } = useContext(AuthContext);
  return role;
}